import { Checkbox, Divider, Group, ScrollArea, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import dayjs from 'dayjs';
import { useFinancialsStore } from '../../../../store/financials-store/financials-store';
import {
  setCostByInitiativeInitiativeIds,
  setCostByInitiativeTrendLines,
} from '../../../../store/financials-store/financials-store.actions';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { newCOLORS } from '../../../../styles/colors';
import { Icon } from '../../../../ui-library/icon/icon';
import { DEFAULT_CHART_COLORS } from '../../constants';
import { useCostByInitiative } from '../../financials.hooks';
import { SmallSquareButton } from '../shared/small-square-button';
import { getInitiativeInfo, getItemColor } from './cost-by-initiative.helpers';

function Filters({ setShow }: { setShow: (show: boolean) => void }) {
  const [isMeasuresOpen, { toggle: toggleMeasures }] = useDisclosure(true);
  const [isTrendsOpen, { toggle: toggleTrends }] = useDisclosure(true);

  const portfolio = useGlobalStore((state) => state.portfolio);
  const teamId = useFinancialsStore((state) => state.team);
  const boardId = useFinancialsStore((state) => state.board);
  const startDate = useFinancialsStore((state) => state.startDate);
  const endDate = useFinancialsStore((state) => state.endDate);

  const { selectedInitiativeIds, selectedTrendLines } = useFinancialsStore((state) => state.filters.costByInitiative);

  const { data } = useCostByInitiative(
    dayjs(startDate).toDate(),
    dayjs(endDate).toDate(),
    portfolio?.id || null,
    teamId,
    boardId,
  );
  const initiativeInfo = getInitiativeInfo(data);

  return (
    <Stack h="100%">
      <Group justify="space-between">
        <Text fw={750}>Filters</Text>
        <SmallSquareButton onClick={() => setShow(false)} iconName="arrow_back_ios" />
      </Group>
      <Divider />
      <Group justify="space-between">
        <Group>
          <Icon name="adjust" size={24} color={newCOLORS.gray} />
          <Text fw={750}>Initiatives</Text>
        </Group>
        <Icon
          name={isMeasuresOpen ? 'unfold_less' : 'unfold_more'}
          size={24}
          color={newCOLORS.gray}
          onClick={toggleMeasures}
          style={{ cursor: 'pointer' }}
        />
      </Group>
      <Divider />
      <ScrollArea h="100%" scrollbarSize={0} style={{ flex: 1 }}>
        {data && isMeasuresOpen ? (
          <Checkbox.Group
            value={selectedInitiativeIds}
            onChange={(value) => setCostByInitiativeInitiativeIds(value as string[])}
          >
            {Object.entries(initiativeInfo).map(([id, name]) => {
              const color = getItemColor(id, selectedInitiativeIds, DEFAULT_CHART_COLORS);
              return (
                <Checkbox
                  key={id}
                  value={id}
                  label={name}
                  mb={8}
                  color={color}
                  styles={{
                    input: { cursor: 'pointer' },
                  }}
                  disabled={
                    selectedInitiativeIds.length >= DEFAULT_CHART_COLORS.length && !selectedInitiativeIds.includes(id)
                  }
                />
              );
            })}
          </Checkbox.Group>
        ) : null}
        {selectedInitiativeIds.length > 0 && (
          <Stack>
            <Divider />
            <Group justify="space-between">
              <Group>
                <Icon name="adjust" size={24} color={newCOLORS.gray} />
                <Text fw={750}>Trends</Text>
              </Group>
              <Icon
                name={isTrendsOpen ? 'unfold_less' : 'unfold_more'}
                size={24}
                color={newCOLORS.gray}
                onClick={toggleTrends}
                style={{ cursor: 'pointer' }}
              />
            </Group>
            <Divider />
            {isTrendsOpen && (
              <Checkbox.Group
                value={selectedTrendLines}
                onChange={(value) => setCostByInitiativeTrendLines(value as string[])}
              >
                {Object.entries(initiativeInfo)
                  .filter(([id]) => selectedInitiativeIds.includes(id))
                  .map(([id, name]) => {
                    const color = getItemColor(id, selectedInitiativeIds, DEFAULT_CHART_COLORS);
                    return (
                      <Checkbox
                        key={id}
                        value={id}
                        label={name}
                        mb={8}
                        color={color}
                        styles={{
                          input: { cursor: 'pointer' },
                        }}
                        disabled={
                          selectedTrendLines.length >= DEFAULT_CHART_COLORS.length && !selectedTrendLines.includes(id)
                        }
                      />
                    );
                  })}
              </Checkbox.Group>
            )}
          </Stack>
        )}
      </ScrollArea>
    </Stack>
  );
}

export { Filters };
